import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IWorkout } from '../../../models/programming';
import {
  BLOCK_TYPE,
  EXERCISE_METHOD,
  WORKOUT_TYPE,
  WORKOUT_CATEGORY,
} from '../../../enums/programming';
import {
  FormExercise,
  FormBlock,
  FormDay,
  emptyLoad,
  emptyExercise,
  emptyBlock,
  emptyDay,
  dayFromModel,
  blockFromModel,
  buildPayload,
  OccurrenceMode,
} from './workoutBuilderTypes';
import { IBlock } from '../../../models/programming';

const SUPERSET_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function useWorkoutBuilder(props: {
  workout: IWorkout | null;
  isOpen: boolean;
  onClose: () => void;
  occurrenceMode?: OccurrenceMode | null;
  onSaved?: (workout: IWorkout) => void;
  initialCategory?: WORKOUT_CATEGORY;
}) {
  const { workout: workoutProp, isOpen, onClose, occurrenceMode, onSaved, initialCategory } = props;
  const queryClient = useQueryClient();
  const { postProgrammingWorkout, patchProgrammingWorkout, getProgrammingWorkout, getAssignmentOccurrence, patchAssignmentOccurrence } = useEndpoints();

  // Fetch full workout by ID (with populated exercises) when editing
  const workoutId = workoutProp?._id;
  const fullWorkoutQuery = useQuery({
    queryKey: ['programming-workout-detail', workoutId],
    queryFn: async () => {
      const { data } = await getProgrammingWorkout(workoutId!);
      return data;
    },
    enabled: isOpen && !!workoutId,
    staleTime: 0, // Always refetch when opening edit
  });

  // Fetch occurrence data when editing a recurrence and workoutData is not pre-populated
  const { data: occurrenceData, isLoading: isLoadingOccurrence } = useQuery({
    queryKey: ['occurrence-edit', occurrenceMode?.assignmentId, occurrenceMode?.eventId],
    queryFn: async () => {
      const { data } = await getAssignmentOccurrence(occurrenceMode!.assignmentId, occurrenceMode!.eventId);
      return data as any;
    },
    enabled: isOpen && !!occurrenceMode && !occurrenceMode.workoutData,
    staleTime: 0,
  });

  // Use fetched workout (with populated exercises) if available, otherwise fallback to prop
  const workout = useMemo(() => {
    if (occurrenceMode) {
      return occurrenceMode.workoutData ?? occurrenceData?.workout ?? null;
    }
    return fullWorkoutQuery.data ?? workoutProp;
  }, [occurrenceMode, occurrenceData, fullWorkoutQuery.data, workoutProp]);

  const isLoadingWorkout = occurrenceMode
    ? isLoadingOccurrence
    : !!workoutId && fullWorkoutQuery.isLoading;

  // --- Form state ---
  const [title, setTitle] = useState('');
  const [workoutType, setWorkoutType] = useState<WORKOUT_TYPE>(
    WORKOUT_TYPE.SINGLE_DAY,
  );
  const [tags, setTags] = useState('');
  const [athleteNotes, setAthleteNotes] = useState('');
  const [isTemplate, setIsTemplate] = useState(true);
  const [workoutCategory, setWorkoutCategory] = useState<WORKOUT_CATEGORY>(WORKOUT_CATEGORY.PERFORMANCE);
  const [activeDay, setActiveDay] = useState(0);
  const [days, setDays] = useState<FormDay[]>([emptyDay(1)]);

  // new block type picker per day
  const [newBlockType, setNewBlockType] = useState<Record<string, BLOCK_TYPE>>(
    {},
  );

  // exercise picker modal: tracks which exercise row triggered the modal
  const [exercisePickerTarget, setExercisePickerTarget] = useState<{
    dayLocalId: string;
    blockLocalId: string;
    exLocalId: string;
  } | null>(null);

  // superset: checkbox selection per block (blockLocalId → Set of exerciseLocalIds)
  const [selectedExercises, setSelectedExercises] = useState<Record<string, Set<string>>>({});

  // collapsed/expanded state for standalone exercises
  const [expandedExercises, setExpandedExercises] = useState<Set<string>>(new Set());
  const toggleExpanded = useCallback((exLocalId: string) => {
    setExpandedExercises(prev => {
      const next = new Set(prev);
      if (next.has(exLocalId)) next.delete(exLocalId);
      else next.add(exLocalId);
      return next;
    });
  }, []);

  // drag-and-drop state for superset creation
  const [activeExercise, setActiveExercise] = useState<{ ex: FormExercise; blockLocalId: string } | null>(null);
  const [overExerciseId, setOverExerciseId] = useState<string | null>(null);

  // help drawer
  const [showHelp, setShowHelp] = useState(false);

  // --- Initialize from workout (fetched or prop) ---
  useEffect(() => {
    if (isOpen && !isLoadingWorkout) {
      if (workout) {
        setTitle(workout.title);
        setWorkoutType(workout.type);
        setTags(workout.tags.join(', '));
        setAthleteNotes(workout.athleteNotes || '');
        setIsTemplate(workout.isTemplate);
        // Il backend serializza il campo come "category"; "workoutCategory" è solo UI legacy
        setWorkoutCategory((workout.category || workout.workoutCategory || WORKOUT_CATEGORY.PERFORMANCE) as WORKOUT_CATEGORY);
        setActiveDay(0);
        setDays(
          workout.days.length > 0
            ? workout.days.map(dayFromModel)
            : [emptyDay(1)],
        );
      } else {
        setTitle('');
        setWorkoutType(WORKOUT_TYPE.SINGLE_DAY);
        setTags('');
        setAthleteNotes('');
        setIsTemplate(true);
        setWorkoutCategory(initialCategory ?? WORKOUT_CATEGORY.PERFORMANCE);
        setActiveDay(0);
        setDays([emptyDay(1)]);
      }
      setNewBlockType({});
    }
  }, [isOpen, workout, isLoadingWorkout]);

  // Sync days when switching type
  useEffect(() => {
    if (workoutType === WORKOUT_TYPE.SINGLE_DAY && days.length > 1) {
      setDays((prev) => [prev[0]]);
      setActiveDay(0);
    }
  }, [workoutType]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Mutations ---
  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(
        title,
        workoutType,
        tags,
        athleteNotes,
        days,
        isTemplate,
        workoutCategory,
      );
      if (occurrenceMode) {
        const { data } = await patchAssignmentOccurrence(
          occurrenceMode.assignmentId,
          occurrenceMode.eventId,
          occurrenceMode.scope,
          payload,
        );
        return data;
      }
      if (workout) {
        const { data } = await patchProgrammingWorkout(workout._id, payload);
        return data;
      }
      const { data } = await postProgrammingWorkout(payload);
      return data;
    },
    onSuccess: (data) => {
      if (occurrenceMode) {
        queryClient.invalidateQueries({ queryKey: ['occurrences'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['programming-workouts'] });
      }
      if (onSaved && data) {
        onSaved(data as IWorkout);
      }
      onClose();
    },
    onError: (err: any) => {
      const apiMsg = err?.response?.data?.message;
      const message = Array.isArray(apiMsg)
        ? apiMsg.join(', ')
        : apiMsg || err?.message || 'Errore durante il salvataggio della scheda';
      toast.error(message);
    },
  });

  // --- Day operations ---
  const addDay = useCallback(() => {
    setDays((prev) => {
      const next = [...prev, emptyDay(prev.length + 1)];
      setActiveDay(next.length - 1);
      return next;
    });
  }, []);

  const removeDay = useCallback(
    (idx: number) => {
      setDays((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((_, i) => i !== idx);
        return next;
      });
      setActiveDay((prev) => Math.max(0, prev > idx ? prev - 1 : Math.min(prev, days.length - 2)));
    },
    [days.length],
  );

  const updateDayTitle = useCallback((dayLocalId: string, value: string) => {
    setDays((prev) =>
      prev.map((d) =>
        d._localId === dayLocalId ? { ...d, title: value } : d,
      ),
    );
  }, []);

  // --- Block operations ---
  const addBlock = useCallback(
    (dayLocalId: string) => {
      const type = newBlockType[dayLocalId] || BLOCK_TYPE.MAX_STRENGTH;
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? { ...d, blocks: [...d.blocks, emptyBlock(type)] }
            : d,
        ),
      );
    },
    [newBlockType],
  );

  const addBlockFromTemplate = useCallback(
    (dayLocalId: string, block: IBlock) => {
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? { ...d, blocks: [...d.blocks, blockFromModel(block)] }
            : d,
        ),
      );
    },
    [],
  );

  const removeBlock = useCallback((dayLocalId: string, blockLocalId: string) => {
    setDays((prev) =>
      prev.map((d) =>
        d._localId === dayLocalId
          ? { ...d, blocks: d.blocks.filter((b) => b._localId !== blockLocalId) }
          : d,
      ),
    );
  }, []);

  const updateBlock = useCallback(
    (dayLocalId: string, blockLocalId: string, patch: Partial<FormBlock>) => {
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? {
                ...d,
                blocks: d.blocks.map((b) =>
                  b._localId === blockLocalId ? { ...b, ...patch } : b,
                ),
              }
            : d,
        ),
      );
    },
    [],
  );

  // --- Exercise operations ---
  const addExercise = useCallback(
    (dayLocalId: string, blockLocalId: string) => {
      const newEx = emptyExercise();
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? {
                ...d,
                blocks: d.blocks.map((b) =>
                  b._localId === blockLocalId
                    ? { ...b, exercises: [...b.exercises, newEx] }
                    : b,
                ),
              }
            : d,
        ),
      );
      setExpandedExercises(prev => {
        const next = new Set(prev);
        next.add(newEx._localId);
        return next;
      });
    },
    [],
  );

  const removeExercise = useCallback(
    (dayLocalId: string, blockLocalId: string, exLocalId: string) => {
      setDays((prev) =>
        prev.map((d) => {
          if (d._localId !== dayLocalId) return d;
          return {
            ...d,
            blocks: d.blocks.map((b) => {
              if (b._localId !== blockLocalId) return b;
              const removedEx = b.exercises.find((ex) => ex._localId === exLocalId);
              const group = removedEx?.supersetGroup;
              let remaining = b.exercises.filter((ex) => ex._localId !== exLocalId);
              // Se nel superset rimane 1 solo esercizio, dissolvi il gruppo
              if (group) {
                const groupRemaining = remaining.filter((ex) => ex.supersetGroup === group);
                if (groupRemaining.length <= 1) {
                  remaining = remaining.map((ex) =>
                    ex.supersetGroup === group ? { ...ex, supersetGroup: '' } : ex,
                  );
                }
              }
              return { ...b, exercises: remaining };
            }),
          };
        }),
      );
    },
    [],
  );

  const updateExercise = useCallback(
    (
      dayLocalId: string,
      blockLocalId: string,
      exLocalId: string,
      patch: Partial<FormExercise>,
    ) => {
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? {
                ...d,
                blocks: d.blocks.map((b) =>
                  b._localId === blockLocalId
                    ? {
                        ...b,
                        exercises: b.exercises.map((ex) =>
                          ex._localId === exLocalId ? { ...ex, ...patch } : ex,
                        ),
                      }
                    : b,
                ),
              }
            : d,
        ),
      );
    },
    [],
  );

  // --- Superset operations ---
  const getNextSupersetLetter = useCallback((block: FormBlock): string => {
    const usedLetters = new Set(
      block.exercises.map((e) => e.supersetGroup).filter(Boolean),
    );
    for (const letter of SUPERSET_LETTERS) {
      if (!usedLetters.has(letter)) return letter;
    }
    return 'A';
  }, []);

  const createSuperset = useCallback(
    (dayLocalId: string, block: FormBlock, exLocalIds: string[]) => {
      const letter = getNextSupersetLetter(block);
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? {
                ...d,
                blocks: d.blocks.map((b) =>
                  b._localId === block._localId
                    ? {
                        ...b,
                        exercises: b.exercises.map((ex) =>
                          exLocalIds.includes(ex._localId)
                            ? { ...ex, supersetGroup: letter }
                            : ex,
                        ),
                      }
                    : b,
                ),
              }
            : d,
        ),
      );
      setSelectedExercises((prev) => ({ ...prev, [block._localId]: new Set() }));
    },
    [getNextSupersetLetter],
  );

  const removeFromSuperset = useCallback(
    (dayLocalId: string, blockLocalId: string, exLocalId: string) => {
      setDays((prev) =>
        prev.map((d) => {
          if (d._localId !== dayLocalId) return d;
          return {
            ...d,
            blocks: d.blocks.map((b) => {
              if (b._localId !== blockLocalId) return b;
              const removedEx = b.exercises.find((ex) => ex._localId === exLocalId);
              const group = removedEx?.supersetGroup;
              let updated = b.exercises.map((ex) =>
                ex._localId === exLocalId ? { ...ex, supersetGroup: '' } : ex,
              );
              // Se nel gruppo rimane 1 solo esercizio, dissolvi il superset
              if (group) {
                const remaining = updated.filter((ex) => ex.supersetGroup === group);
                if (remaining.length <= 1) {
                  updated = updated.map((ex) =>
                    ex.supersetGroup === group ? { ...ex, supersetGroup: '' } : ex,
                  );
                }
              }
              return { ...b, exercises: updated };
            }),
          };
        }),
      );
    },
    [],
  );

  const updateSupersetSets = useCallback(
    (dayLocalId: string, blockLocalId: string, group: string, sets: string) => {
      setDays((prev) =>
        prev.map((d) =>
          d._localId === dayLocalId
            ? {
                ...d,
                blocks: d.blocks.map((b) =>
                  b._localId === blockLocalId
                    ? {
                        ...b,
                        exercises: b.exercises.map((ex) =>
                          ex.supersetGroup === group ? { ...ex, sets } : ex,
                        ),
                      }
                    : b,
                ),
              }
            : d,
        ),
      );
    },
    [],
  );

  const toggleExerciseSelection = useCallback(
    (blockLocalId: string, exLocalId: string) => {
      setSelectedExercises((prev) => {
        const current = prev[blockLocalId] ?? new Set<string>();
        const next = new Set(current);
        if (next.has(exLocalId)) {
          next.delete(exLocalId);
        } else {
          next.add(exLocalId);
        }
        return { ...prev, [blockLocalId]: next };
      });
    },
    [],
  );

  // --- Drag-and-drop handlers ---
  const handleDragStart = useCallback((event: DragStartEvent, block: FormBlock) => {
    const draggedEx = block.exercises.find((ex) => ex._localId === event.active.id);
    if (draggedEx) {
      setActiveExercise({ ex: draggedEx, blockLocalId: block._localId });
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id as string | undefined;
    if (overId && overId.startsWith('drop-superset-')) {
      setOverExerciseId(overId);
    } else if (overId && overId.startsWith('drop-')) {
      setOverExerciseId(overId.replace('drop-', ''));
    } else {
      setOverExerciseId(null);
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent, dayLocalId: string, block: FormBlock) => {
      setActiveExercise(null);
      setOverExerciseId(null);

      const draggedId = event.active.id as string;
      const overId = event.over?.id as string | undefined;
      if (!overId || !overId.startsWith('drop-')) return;

      // Handle drop onto a superset group
      if (overId.startsWith('drop-superset-')) {
        const groupKey = overId.replace('drop-superset-', '');
        // groupKey format: `${block._localId}-${supersetGroup}`
        const supersetLetter = groupKey.split('-').pop();
        const draggedEx = block.exercises.find((ex) => ex._localId === draggedId);
        if (supersetLetter && draggedEx && !draggedEx.supersetGroup) {
          setDays((prev) =>
            prev.map((d) =>
              d._localId === dayLocalId
                ? {
                    ...d,
                    blocks: d.blocks.map((b) =>
                      b._localId === block._localId
                        ? {
                            ...b,
                            exercises: b.exercises.map((ex) =>
                              ex._localId === draggedId
                                ? { ...ex, supersetGroup: supersetLetter }
                                : ex,
                            ),
                          }
                        : b,
                    ),
                  }
                : d,
            ),
          );
        }
        return;
      }

      const targetId = overId.replace('drop-', '');
      if (draggedId === targetId) return;

      const draggedEx = block.exercises.find((ex) => ex._localId === draggedId);
      const targetEx = block.exercises.find((ex) => ex._localId === targetId);
      if (!draggedEx || !targetEx) return;

      // If target already belongs to a superset group, add dragged to that group
      if (targetEx.supersetGroup) {
        setDays((prev) =>
          prev.map((d) =>
            d._localId === dayLocalId
              ? {
                  ...d,
                  blocks: d.blocks.map((b) =>
                    b._localId === block._localId
                      ? {
                          ...b,
                          exercises: b.exercises.map((ex) =>
                            ex._localId === draggedId
                              ? { ...ex, supersetGroup: targetEx.supersetGroup }
                              : ex,
                          ),
                        }
                      : b,
                  ),
                }
              : d,
          ),
        );
        return;
      }

      // If dragged already belongs to a superset group, add target to that group
      if (draggedEx.supersetGroup) {
        setDays((prev) =>
          prev.map((d) =>
            d._localId === dayLocalId
              ? {
                  ...d,
                  blocks: d.blocks.map((b) =>
                    b._localId === block._localId
                      ? {
                          ...b,
                          exercises: b.exercises.map((ex) =>
                            ex._localId === targetId
                              ? { ...ex, supersetGroup: draggedEx.supersetGroup }
                              : ex,
                          ),
                        }
                      : b,
                  ),
                }
              : d,
          ),
        );
        return;
      }

      // Both standalone: create a new superset group
      createSuperset(dayLocalId, block, [draggedId, targetId]);
    },
    [createSuperset],
  );

  // --- Helpers ---
  // PYRAMID e DROPSET vengono gestiti tramite la griglia per-serie (PerSetGrid),
  // non tramite MethodFieldsAdapter — rimangono quindi nel ramo "standard".
  const VARIABLE_SET_METHODS_SET = new Set<EXERCISE_METHOD | ''>([
    EXERCISE_METHOD.PYRAMID,
    EXERCISE_METHOD.DROPSET,
  ]);
  const isNonStandardMethod = (method: EXERCISE_METHOD | '') =>
    method !== '' &&
    method !== EXERCISE_METHOD.STANDARD &&
    !VARIABLE_SET_METHODS_SET.has(method);

  // --- Computed ---
  const estimatedDuration = workout?.estimatedDuration;
  const isEdit = !!workout;

  // Validazione strutturale: deve riflettere ciò che il backend accetta
  // (ogni blocco esige >=1 esercizio con exerciseId, vedi buildPayload).
  const saveDisabledReason = useMemo(() => {
    if (saveMutation.isPending) return null;
    if (title.trim().length === 0) return 'Inserisci un titolo';
    if (days.length === 0) return 'Aggiungi almeno un giorno';
    for (let dayIdx = 0; dayIdx < days.length; dayIdx++) {
      const day = days[dayIdx];
      const dayLabel = day.title.trim() || `Giorno ${dayIdx + 1}`;
      if (day.blocks.length === 0) {
        return `Aggiungi almeno un blocco a ${dayLabel}`;
      }
      for (const block of day.blocks) {
        const hasValidExercise = block.exercises.some((ex) => ex.exerciseId);
        if (!hasValidExercise) {
          const blockLabel = block.title.trim() || 'blocco senza titolo';
          return `Seleziona almeno un esercizio nel blocco "${blockLabel}" (${dayLabel})`;
        }
      }
    }
    return null;
  }, [title, days, saveMutation.isPending]);

  const canSave = saveDisabledReason === null && !saveMutation.isPending;
  const currentDay = days[activeDay] ?? days[0];

  return {
    // state values
    title, setTitle,
    workoutType, setWorkoutType,
    tags, setTags,
    athleteNotes, setAthleteNotes,
    isTemplate, setIsTemplate,
    workoutCategory, setWorkoutCategory,
    activeDay, setActiveDay,
    days,
    newBlockType, setNewBlockType,
    exercisePickerTarget, setExercisePickerTarget,
    selectedExercises,
    expandedExercises, setExpandedExercises,
    toggleExpanded,
    activeExercise,
    overExerciseId,
    showHelp, setShowHelp,
    // callbacks
    addDay, removeDay, updateDayTitle,
    addBlock, addBlockFromTemplate, removeBlock, updateBlock,
    addExercise, removeExercise, updateExercise,
    createSuperset, removeFromSuperset, updateSupersetSets,
    toggleExerciseSelection,
    handleDragStart, handleDragOver, handleDragEnd,
    // computed
    estimatedDuration, isEdit, canSave, saveDisabledReason, currentDay, saveMutation,
    isNonStandardMethod, isLoadingWorkout, occurrenceMode,
  };
}
